import { useEffect, useState } from "react";
import { hasRole, useApi, useApp } from "../../context/AppContext";
import { ago } from "../../lib/fleet";

// Releases (docs/tasks/35, phase 7): what code the cabinet runs, what it is
// told to run and how far the agent got, and assigning a release. Building and
// publishing a release stays in tools/publish_code_release.sh.

const PHASE_TONE = { active: "text-emerald-700", rolled_back: "text-rose-700", failed: "text-rose-700" };

export function ReleasesTab({ deviceId, health }) {
  const api = useApi();
  const { user } = useApp();
  const [rollout, setRollout] = useState(undefined);
  const [releases, setReleases] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const may = hasRole(user, "tenant_admin", "ml_operator");
  const r = health?.report || {};
  const running = r.system?.code_version;
  const atCabinet = r.door?.state === "open" || (r.session && r.session.state !== "closed");

  const load = () => api.getCodeDeploymentCurrent(deviceId).then((d) => setRollout(d.rollout)).catch(() => setRollout(null));
  useEffect(() => {
    void load();
    api.listCodeReleases().then((list) => setReleases(list.filter((x) => (x.kind || "runtime") === "runtime" && x.status === "ready")
      .sort((a, b) => (a.version < b.version ? 1 : -1)))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, deviceId]);
  useEffect(() => {
    if (!rollout || ["active", "rolled_back", "failed", null, undefined].includes(rollout.phase)) return undefined;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rollout]);

  const assign = async (body, text) => {
    if (atCabinet) { setMsg({ bad: true, text: "The door is open or a visit is running: a release restarts the runtime. Wait until it ends." }); return; }
    if (!window.confirm(text)) return;
    setBusy(true); setMsg(null);
    try {
      await (body.rollback ? api.request(`/devices/${deviceId}/code-deployments`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) })
        : api.assignCodeRelease(deviceId, body.version));
      setMsg({ text: "Assigned. The agent installs it within a couple of minutes, with a health check and an automatic rollback." });
      await load();
    } catch (e) {
      setMsg({ bad: true, text: String(e.message || e) });
    } finally {
      setBusy(false);
    }
  };
  const top = releases[0]?.version;
  return (
    <div className="space-y-4">
      <div className="card">
        <h2 className="mb-2 text-sm font-semibold">Code on this cabinet</h2>
        <dl className="grid grid-cols-[11rem_1fr] gap-y-1 text-sm">
          <dt className="text-slate-500">Running</dt>
          <dd className="font-mono text-xs">{running || "—"}{running && top && running < top ? <span className="font-sans text-amber-700"> · the newest release is {top}</span> : running ? <span className="font-sans text-emerald-700"> · the newest</span> : null}</dd>
          <dt className="text-slate-500">Assigned</dt><dd className="font-mono text-xs">{rollout?.desired_version || "nothing"}</dd>
          <dt className="text-slate-500">Rollout</dt>
          <dd className="text-xs">
            {/* The agent sets reported_version when a release is active, but on dev the phase can stay "queued" after it (22 Oct). */}
            {rollout ? (rollout.reported_version && rollout.reported_version === rollout.desired_version
              ? <span className="text-emerald-700">installed{rollout.phase && rollout.phase !== "active" ? ` (the agent left the phase at "${rollout.phase}")` : ""}</span>
              : <span className={PHASE_TONE[rollout.phase] || ""}>{rollout.phase || "—"}</span>) : "never assigned"}
            {rollout?.reported_version ? ` · reported ${rollout.reported_version}` : ""}{rollout?.updated_at ? ` · ${ago(rollout.updated_at)}` : ""}
            {rollout?.error ? <span className="text-rose-700"> · {rollout.error}</span> : null}
          </dd>
          <dt className="text-slate-500">Agent</dt><dd className="font-mono text-xs">{rollout?.reported_agent_version || "—"}{rollout?.desired_agent_version ? ` (assigned ${rollout.desired_agent_version})` : ""}</dd>
        </dl>
      </div>
      {may && (
        <div className="card">
          <h2 className="mb-2 text-sm font-semibold">Assign a release</h2>
          <div className="flex flex-wrap items-center gap-2">
            <select className="input w-auto" value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">choose…</option>
              {releases.slice(0, 15).map((x) => <option key={x.version} value={x.version}>{x.version}{x.notes ? ` — ${String(x.notes).slice(0, 60)}` : ""}</option>)}
            </select>
            <button className="btn btn-primary" disabled={!pick || busy}
              onClick={() => assign({ version: pick }, `Install ${pick} on this cabinet? The runtime restarts for about a minute; a failed health check rolls it back.`)}>Assign</button>
            {rollout?.reported_version && (
              <button className="btn btn-secondary" disabled={busy}
                onClick={() => assign({ rollback: true }, "Roll this cabinet back to the version it ran before?")}>Roll back</button>
            )}
          </div>
          {msg && <p className={`mt-2 text-xs ${msg.bad ? "text-rose-700" : "text-emerald-700"}`}>{msg.text}</p>}
          <p className="mt-2 text-xs text-slate-500">Never while someone is at the cabinet: a release restarts the runtime (a release killed a training visit on 7 October).</p>
        </div>
      )}
    </div>
  );
}
